// The Published tab: every version the workflow has been published as, and
// what each one asked — the current one selected, so the tab opens on what
// the share link serves right now.
//
// The draft's own preview is the Preview tab beside this one; this is its
// counterpart over the frozen definitions, which is what lets somebody
// compare what the link serves with what they are about to publish. A
// version's preview is the same component over the same runtime, so the two
// can only differ where the definitions do.
import React, { useEffect, useMemo, useState } from "react";
import { Nav, NavItem, NavLink, Badge } from "reactstrap";
import { useQuery } from "@apollo/client";
import { Icon as LoadingIcon } from "@repoflow/components/loading";
import { Preview } from "components/steps-preview";
import * as widgets from "@shared/widgets/register";
import * as WorkflowVersionQL from "queries/workflow-version";
import { when } from "./publish";

const NONE = [];

export const VersionsTab = ({ workflow }) => {
  const { workflowId } = workflow;
  const publishedVersion = workflow.publishedVersion || 0;

  // The first page is every version a workflow is likely to have; a workflow
  // published more than a hundred times reads its oldest through the
  // versions namespace and not through this tab.
  const { data, loading, error } = useQuery(WorkflowVersionQL.list, {
    variables: { workflowId, input: { limit: 100 } },
    // A publish a moment ago evicted the listing; this is what reads it back.
    fetchPolicy: "cache-and-network",
    skip: !publishedVersion,
  });

  const versions = useMemo(
    () => (data ? data.viewer.account.workflows.get.versions.list.items : NONE),
    [data]
  );

  // Selected by NUMBER, and the published one until somebody picks another —
  // including after a publish, which moves the published number and should
  // move the selection with it.
  const [selected, setSelected] = useState(publishedVersion);
  useEffect(() => setSelected(publishedVersion), [publishedVersion]);
  const version = versions.find((v) => v.version === selected) || versions[0];

  if (!publishedVersion) {
    return (
      <p id="workflow-published" className="text-muted mt-3">
        Not published yet. Publish the workflow and the version the share link
        serves will be shown here.
      </p>
    );
  }

  if (error) {
    return (
      <p id="workflow-published" className="text-danger mt-3">
        The versions could not be read. Please try again.
      </p>
    );
  }

  if (!version) {
    return (
      <p id="workflow-published" className="text-muted mt-3">
        {loading ? <LoadingIcon /> : "No versions found."}
      </p>
    );
  }

  return (
    <div id="workflow-published" className="mt-3">
      <Nav pills id="workflow-versions" className="mb-3">
        {versions.map((v) => (
          <NavItem key={v.version}>
            <NavLink
              href="#"
              active={v.version === version.version}
              data-version={v.version}
              onClick={(e) => {
                e.preventDefault();
                setSelected(v.version);
              }}
            >
              Version {v.version}
              {v.version === publishedVersion ? (
                <>
                  {" "}
                  <Badge color="success" pill>
                    live
                  </Badge>
                </>
              ) : null}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <div id="workflow-version" data-version={version.version}>
        <h5 className="mb-1">
          {version.title}{" "}
          <small className="text-muted">
            version {version.version}, published {when(version.publishedAt)}
          </small>
        </h5>
        {version.description ? <p className="text-muted">{version.description}</p> : null}
        <Preview
          steps={version.steps || NONE}
          subforms={version.subforms || NONE}
          widgets={widgets}
          empty="This version has no steps: the link says so instead of showing a form."
        />
      </div>
    </div>
  );
};
